import React, { useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useErisInformationStore } from 'src/api/eris/store/ErisInformationStore';
import { useErisUserStore } from 'src/api/eris/store/ErisUserStore';
import { ALL_GROUPS } from 'src/api/eris/constants';
import useGroupService from 'src/api/eris/service/ErisInformationService';
import useDisasterService from 'src/api/eris/service/ErisDisasterService';
import { ERIS_Screen } from 'src/ui/eris/router/ErisScreen';
import { formatUnixSeconds } from 'src/common/date/customDate';
// Components
import RightTriangleIcon from '../../common/icons/RightTriangleIcon';
import { LinkButton } from '../../common/button/LinkButton';
import Win95Button from '../../common/button/Win95Button';
import DropdownSelect from '../../common/select/DropdownSelect';
import { FormRow } from '../../common/form/FormRow';

type ErisSearchComponentProps = {
  showStatus: boolean;
  setShowStatus: (show: boolean) => void;
  showSummary: boolean;
  setShowSummary: (show: boolean) => void;
};

const ErisSearchComponent = ({
  showStatus,
  setShowStatus,
  showSummary,
  setShowSummary,
}: ErisSearchComponentProps) => {
  const { t } = useTranslation();
  const { user } = useErisUserStore();
  const {
    availableGroups,
    selectedGroup,
    setSelectedGroup,
    statusSearchingParameters,
    latestDisasterUpdate,
    statusResult,
    statusSummaryList,
    setStatusResult,
    setStatusSummaryList,
  } = useErisInformationStore();

  const { searchGroups, searchGroupStatus, searchGroupSummary } = useGroupService();
  const { getLatestDisaster, getPreviousDisaster } = useDisasterService();

  useEffect(() => {
    searchGroups();
    getLatestDisaster();
    getPreviousDisaster();
    setSelectedGroup(null);
  }, [getLatestDisaster, getPreviousDisaster, searchGroups, setSelectedGroup]);

  const getLatestParams = useCallback(
    (group: { id: string; name: string; userId: string } | null) => ({
      ...statusSearchingParameters,
      pageNumber: 0,
      searchParams: {
        ...statusSearchingParameters.searchParams,
        buildingName: '',
        operationStatus: undefined,
        groupName: group?.name ?? null,
        userId: user?.id ?? '',
      },
    }),
    [statusSearchingParameters, user?.id]
  );

  const handleSelectedGroup = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    let groupToSelect: { id: string; name: string; userId: string } | null = null;

    if (value && value !== ALL_GROUPS) {
      const foundGroup = availableGroups.find((g) => g.id === value);
      if (foundGroup) {
        groupToSelect = { ...foundGroup, userId: user?.id ?? '' };
      }
    }

    setSelectedGroup(groupToSelect);
    const params = getLatestParams(groupToSelect);

    if (showStatus) {
      searchGroupStatus(params);
    } else if (showSummary) {
      searchGroupSummary(params);
    }
  };

  const handleAction = (type: 'status' | 'summary') => {
    const isStatus = type === 'status';
    setShowStatus(isStatus);
    setShowSummary(!isStatus);

    const params = getLatestParams(selectedGroup);

    isStatus ? searchGroupStatus(params) : searchGroupSummary(params);
    getLatestDisaster();
  };

  const groupOptions = useMemo(
    () => availableGroups.map((group) => ({ title: group.name, value: group.id })),
    [availableGroups]
  );

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <header>
        <div className="flex items-center gap-2">
          <RightTriangleIcon />
          <button
            className="text-xl font-bold hover:text-blue-600"
            onClick={() => {
              setStatusResult(null);
              setStatusSummaryList(null);
              setShowStatus(false);
              setShowSummary(false);
              getLatestDisaster();
            }}
          >
            {t('eris.search.home')}
          </button>
        </div>
        <div className="mt-1 h-1 w-full bg-gradient-to-r from-blue-400 to-blue-700" />
      </header>

      {/* Info Section */}
      <div className="flex items-center justify-between px-1">
        <h2 className="text-2xl font-bold">{t('eris.search.recoveryInformation')}</h2>
        <span className="text-sm">
          {t('eris.search.lastUpdate')}: {formatUnixSeconds(latestDisasterUpdate?.lastUpdateDate)}
        </span>
      </div>

      <div className="flex flex-col gap-3 ps-6">
        <FormRow label={t('eris.search.supportStartTime')}>
          {latestDisasterUpdate?.startDate && (
            <span>
              {formatUnixSeconds(latestDisasterUpdate.startDate)}
              {latestDisasterUpdate.stopDate
                ? ` [${t('eris.search.recoveryComplete')}]`
                : ` [${t('eris.search.inProgress')}]`}
            </span>
          )}
        </FormRow>

        <FormRow label={t('eris.search.groupName')}>
          <DropdownSelect
            initialOption={{ title: t('eris.search.allGroups'), value: ALL_GROUPS }}
            selectedValue={
              selectedGroup
                ? { title: selectedGroup.name, value: selectedGroup.id }
                : { title: t('eris.search.allGroups'), value: ALL_GROUPS }
            }
            optionValues={groupOptions}
            onChange={handleSelectedGroup}
          />
        </FormRow>
        <div className="flex justify-end gap-4">
          <Win95Button
            title={t('eris.search.supportStatusList')}
            onClick={() => handleAction('status')}
          />
          <Win95Button
            title={t('eris.search.supportStatusTotal')}
            onClick={() => handleAction('summary')}
          />
        </div>
      </div>

      <hr className="border-gray-400" />

      {/* Footer Actions */}
      {statusResult === null && (statusSummaryList?.groupSummaries ?? []).length === 0 && (
        <div className="flex justify-end">
          <LinkButton screen={ERIS_Screen.History} label={t('eris.search.history')} />
        </div>
      )}
    </div>
  );
};

export default ErisSearchComponent;
